// @oasis-permission-inherits: /ai-edits (studio-edits:view)
import { useQueries } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CopyIcon,
  ExternalLinkIcon,
  RefreshCwIcon,
} from "lucide-react";
import { type CSSProperties, Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { writeClipboardText } from "~/lib/clipboard";
import { WorkflowAssetPreview } from "~/modules/cactus-generations/components/workflow-asset-preview";
import {
  fetchLongToShortProjectGroupQuery,
  LONG_TO_SHORT_SEARCH_PAGE_SIZE,
  searchLongToShortProjectsQuery,
} from "~/modules/long-to-short/queries";
import {
  LongToShortEditState,
  LongToShortSearchItem,
  ProjectGroup,
  ProjectGroupMetadata,
  ProjectGroupItem,
  longToShortEditStateSchema,
} from "~/modules/long-to-short/schemas";
import { optionalTrueSearchParamSchema } from "~/modules/long-to-short/search-params";
import { lookupUserByIdQuery, internalTestAccountsQuery } from "~/modules/lookup/queries";

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_LOOKBACK_MS = 7 * DAY_MS;
const ALL_STATUSES = "all";
const DEV_TEST_USER_IDS = new Set(["dev-user-id", "dev-test-user"]);
const DEV_TEST_USER_ID_PATTERNS = [
  /^test(?:[-_]|$)/i,
  /(?:^|[-_])dev[-_]?test(?:[-_]|$)/i,
  /(?:^|[-_])internal[-_]?test(?:[-_]|$)/i,
  /(?:^|[-_])automated[-_]?test(?:[-_]|$)/i,
];

const searchSchema = z.object({
  updatedAfterMs: z.coerce.number().optional(),
  updatedBeforeMs: z.coerce.number().optional(),
  userId: z.string().optional(),
  projectGroupId: z.string().optional(),
  status: longToShortEditStateSchema.optional(),
  expandedResultId: z.string().optional(),
  excludeInternalUsers: optionalTrueSearchParamSchema,
});

type LongToShortRouteSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_dash/ai-edits/long-to-short")({
  component: RouteComponent,
  validateSearch: searchSchema,
});

function defaultDateRange() {
  const now = Date.now();
  return {
    updatedAfterMs: now - DEFAULT_LOOKBACK_MS,
    updatedBeforeMs: now,
  };
}

function dateInputValue(ms: number) {
  const date = new Date(ms);
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function dateInputToMs(value: string) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.getTime();
}

function formatDate(value?: string | null) {
  if (!value) {
    return "Unknown";
  }
  return new Date(value).toLocaleString();
}

function formatDuration(ms: number | null) {
  if (ms === null || ms < 0) {
    return "Unknown";
  }
  if (ms < 1000) {
    return `${ms}ms`;
  }
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

function formatPlatform(platform: LongToShortSearchItem["platform"]) {
  return platform === "ios" ? "iOS" : "Web";
}

function formatSourceTimestamp(ms: number) {
  if (ms < 0) {
    return "Unknown";
  }
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function latencyMs(item: LongToShortSearchItem) {
  if (!item.createdAt || !item.updatedAt) {
    return null;
  }
  const created = new Date(item.createdAt).getTime();
  const updated = new Date(item.updatedAt).getTime();
  if (Number.isNaN(created) || Number.isNaN(updated)) {
    return null;
  }
  return updated - created;
}

function stateBadgeClass(state: LongToShortEditState) {
  if (state === "COMPLETE") {
    return "bg-green-600 text-white";
  }
  if (state === "FAILED" || state === "CANCELED") {
    return "bg-red-600 text-white";
  }
  if (state === "PROCESSING") {
    return "bg-blue-600 text-white";
  }
  return "bg-zinc-600 text-white";
}

function truncateMiddle(value: string, size = 8) {
  if (value.length <= size * 2 + 1) {
    return value;
  }
  return `${value.slice(0, size)}...${value.slice(-size)}`;
}

function displayId(value?: string | null, fallback = "None") {
  return value ? truncateMiddle(value) : fallback;
}

async function copyId(value: string, label: string) {
  try {
    await writeClipboardText(value);
    toast.success(`${label} copied to clipboard`);
  } catch {
    toast.error(`Failed to copy ${label}`);
  }
}

function CopyableId({
  value,
  label,
  fallback = "None",
  prefix,
}: {
  value?: string | null;
  label: string;
  fallback?: string;
  prefix?: string;
}) {
  if (!value) {
    return <span>{fallback}</span>;
  }

  return (
    <button
      type="button"
      className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-sm text-left font-mono text-xs hover:bg-muted"
      title={`Copy ${label}: ${value}`}
      aria-label={`Copy ${label}: ${value}`}
      data-copy-id-value={value}
      data-copy-id-label={label}
      onPointerDown={(event) => {
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void copyId(value, label);
      }}
    >
      {prefix && <span className="shrink-0 font-sans text-muted-foreground">{prefix}</span>}
      <span className="truncate">{displayId(value)}</span>
      <CopyIcon className="size-3 shrink-0 text-muted-foreground" />
    </button>
  );
}

function projectGroupSource(metadata: LongToShortSearchItem["projectGroupMetadata"]) {
  if (metadata?.sourceUrl) {
    return { value: metadata.sourceUrl, label: "Source URL", prefix: "URL" };
  }
  if (metadata?.sourceAssetId) {
    return { value: metadata.sourceAssetId, label: "Source asset ID", prefix: "Asset" };
  }
  return null;
}

function buildProjectViewerReturnSearch({
  search,
  updatedAfterMs,
  updatedBeforeMs,
  activeExpandedResultId,
}: {
  search: LongToShortRouteSearch;
  updatedAfterMs: number;
  updatedBeforeMs: number;
  activeExpandedResultId?: string;
}) {
  return {
    from: "long-to-short" as const,
    updatedAfterMs,
    updatedBeforeMs,
    userId: search.userId,
    projectGroupId: search.projectGroupId,
    status: search.status,
    expandedResultId: activeExpandedResultId,
    excludeInternalUsers: search.excludeInternalUsers,
  };
}

function parseAspectRatio(value: string) {
  const ratioParts = /^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/.exec(value.trim());
  if (ratioParts) {
    const width = Number(ratioParts[1]);
    const height = Number(ratioParts[2]);
    if (width > 0 && height > 0) {
      return width / height;
    }
  }

  const numericRatio = Number(value);
  return numericRatio > 0 ? numericRatio : 1;
}

function previewStyleForAspectRatio(value: string): CSSProperties {
  const ratio = parseAspectRatio(value);

  if (ratio >= 1) {
    return {
      width: "min(100%, 448px)",
      aspectRatio: `${ratio}`,
    };
  }

  const height = 384;
  const width = Math.max(176, Math.round(height * ratio));
  return {
    width: `min(100%, ${width}px)`,
    aspectRatio: `${ratio}`,
  };
}

function isDevTestUserId(userId?: string | null) {
  if (!userId) {
    return false;
  }

  const normalized = userId.trim().toLowerCase();
  return (
    DEV_TEST_USER_IDS.has(normalized) ||
    DEV_TEST_USER_ID_PATTERNS.some((pattern) => pattern.test(normalized))
  );
}

function isExcludedInternalAccountType(
  accountType?: { type: string; removedAt?: string | null } | null
) {
  return (
    !accountType?.removedAt &&
    (accountType?.type === "employee" || accountType?.type === "internalAutomatedTester")
  );
}

function shouldExcludeUserId(
  userId: string | null | undefined,
  excludedUserIds: ReadonlySet<string>
) {
  return isDevTestUserId(userId) || (userId ? excludedUserIds.has(userId) : false);
}

function itemMatchesFilters(
  item: LongToShortSearchItem,
  filters: {
    userId?: string;
    projectGroupId?: string;
    status?: LongToShortEditState;
  }
) {
  if (filters.userId && item.userId !== filters.userId) {
    return false;
  }
  if (filters.projectGroupId && item.projectGroupId !== filters.projectGroupId) {
    return false;
  }
  if (filters.status && item.editState !== filters.status) {
    return false;
  }
  return true;
}

function hasClientFilters(search: LongToShortRouteSearch) {
  return Boolean(
    search.userId || search.projectGroupId || search.status || search.excludeInternalUsers
  );
}

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const defaults = useMemo(() => defaultDateRange(), []);
  const updatedAfterMs = search.updatedAfterMs ?? defaults.updatedAfterMs;
  const updatedBeforeMs = search.updatedBeforeMs ?? defaults.updatedBeforeMs;
  const excludeInternalUsers = search.excludeInternalUsers === true;

  const [updatedAfterInput, setUpdatedAfterInput] = useState(dateInputValue(updatedAfterMs));
  const [updatedBeforeInput, setUpdatedBeforeInput] = useState(dateInputValue(updatedBeforeMs));
  const [userIdInput, setUserIdInput] = useState(search.userId ?? "");
  const [projectGroupIdInput, setProjectGroupIdInput] = useState(search.projectGroupId ?? "");
  const [statusInput, setStatusInput] = useState<LongToShortEditState | typeof ALL_STATUSES>(
    search.status ?? ALL_STATUSES
  );

  useEffect(() => {
    setUpdatedAfterInput(dateInputValue(updatedAfterMs));
    setUpdatedBeforeInput(dateInputValue(updatedBeforeMs));
    setUserIdInput(search.userId ?? "");
    setProjectGroupIdInput(search.projectGroupId ?? "");
    setStatusInput(search.status ?? ALL_STATUSES);
  }, [search.projectGroupId, search.status, search.userId, updatedAfterMs, updatedBeforeMs]);

  const { data, isLoading, isFetching, error, refetch } = searchLongToShortProjectsQuery.useQuery({
    params: {
      updatedAfterMs,
      updatedBeforeMs,
      pageSize: LONG_TO_SHORT_SEARCH_PAGE_SIZE,
    },
  });
  const { data: internalAccountsData, isFetching: isFetchingInternalAccounts } =
    internalTestAccountsQuery.useQuery({
      enabled: excludeInternalUsers,
    });

  const baseItems = useMemo(() => {
    const rawItems = data?.items ?? [];
    return rawItems.filter((item) =>
      itemMatchesFilters(item, {
        userId: search.userId,
        projectGroupId: search.projectGroupId,
        status: search.status,
      })
    );
  }, [data?.items, search.projectGroupId, search.status, search.userId]);

  const userIdsForInternalLookup = useMemo(() => {
    if (!excludeInternalUsers) {
      return [];
    }

    const next = new Set<string>();
    for (const item of baseItems) {
      if (item.userId && !isDevTestUserId(item.userId)) {
        next.add(item.userId);
      }
    }
    return Array.from(next);
  }, [baseItems, excludeInternalUsers]);

  const userLookupResults = useQueries({
    queries: userIdsForInternalLookup.map((userId) =>
      lookupUserByIdQuery.queryOptions(
        { pathParams: { userId } },
        { enabled: excludeInternalUsers, retry: false }
      )
    ),
  });

  const excludedInternalUserIds = useMemo(() => {
    const next = new Set<string>();

    for (const account of internalAccountsData?.accounts ?? []) {
      if (account.userId && isExcludedInternalAccountType(account.internalAccountType)) {
        next.add(account.userId);
      }
    }

    for (const result of userLookupResults) {
      const user = result.data?.user;
      if (user && isExcludedInternalAccountType(user.internalAccountType)) {
        next.add(user.userId);
      }
    }

    return next;
  }, [internalAccountsData?.accounts, userLookupResults]);

  const isFetchingInternalUserLookups = userLookupResults.some((result) => result.isFetching);

  const items = useMemo(() => {
    if (!excludeInternalUsers) {
      return baseItems;
    }

    return baseItems.filter((item) => !shouldExcludeUserId(item.userId, excludedInternalUserIds));
  }, [baseItems, excludeInternalUsers, excludedInternalUserIds]);

  const activeExpandedResultId = search.expandedResultId;
  const hasActiveClientFilters = hasClientFilters(search);
  const isResultPageSaturated = (data?.items.length ?? 0) >= LONG_TO_SHORT_SEARCH_PAGE_SIZE;

  const projectViewerReturnSearch = useMemo(
    () =>
      buildProjectViewerReturnSearch({
        search,
        updatedAfterMs,
        updatedBeforeMs,
        activeExpandedResultId,
      }),
    [activeExpandedResultId, search, updatedAfterMs, updatedBeforeMs]
  );

  const updateSearch = (next: Partial<LongToShortRouteSearch>) => {
    navigate({
      search: {
        ...search,
        ...next,
      },
      resetScroll: false,
    });
  };

  const applyFilters = () => {
    const nextUpdatedAfterMs = dateInputToMs(updatedAfterInput);
    const nextUpdatedBeforeMs = dateInputToMs(updatedBeforeInput);
    navigate({
      search: {
        updatedAfterMs: nextUpdatedAfterMs,
        updatedBeforeMs: nextUpdatedBeforeMs,
        userId: userIdInput.trim() || undefined,
        projectGroupId: projectGroupIdInput.trim() || undefined,
        status: statusInput === ALL_STATUSES ? undefined : statusInput,
        excludeInternalUsers: excludeInternalUsers ? true : undefined,
        expandedResultId: undefined,
      },
    });
  };

  const clearFilters = () => {
    const nextDefaults = defaultDateRange();
    setUpdatedAfterInput(dateInputValue(nextDefaults.updatedAfterMs));
    setUpdatedBeforeInput(dateInputValue(nextDefaults.updatedBeforeMs));
    setUserIdInput("");
    setProjectGroupIdInput("");
    setStatusInput(ALL_STATUSES);
    navigate({ search: {} });
  };

  const toggleExpanded = (expandedResultId: string) => {
    updateSearch({
      expandedResultId: activeExpandedResultId === expandedResultId ? undefined : expandedResultId,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold">Long-to-Short Results</h1>
          <p className="text-sm text-muted-foreground">
            Production requests from Project Search with project-group expansion
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCwIcon className="size-4" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[180px_180px_160px_160px_150px_210px_auto_auto]">
            <div className="space-y-1">
              <label htmlFor="lts-updated-after" className="text-xs font-medium">
                Updated After
              </label>
              <Input
                id="lts-updated-after"
                type="datetime-local"
                value={updatedAfterInput}
                onChange={(event) => setUpdatedAfterInput(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="lts-updated-before" className="text-xs font-medium">
                Updated Before
              </label>
              <Input
                id="lts-updated-before"
                type="datetime-local"
                value={updatedBeforeInput}
                onChange={(event) => setUpdatedBeforeInput(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="lts-user-id" className="text-xs font-medium">
                User ID
              </label>
              <Input
                id="lts-user-id"
                value={userIdInput}
                onChange={(event) => setUserIdInput(event.target.value)}
                placeholder="user..."
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="lts-project-group-id" className="text-xs font-medium">
                Project Group ID
              </label>
              <Input
                id="lts-project-group-id"
                value={projectGroupIdInput}
                onChange={(event) => setProjectGroupIdInput(event.target.value)}
                placeholder="project group..."
              />
            </div>
            <div className="space-y-1">
              <span className="text-xs font-medium">Status</span>
              <Select
                value={statusInput}
                onValueChange={(value) =>
                  setStatusInput(value as LongToShortEditState | typeof ALL_STATUSES)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_STATUSES}>All</SelectItem>
                  {longToShortEditStateSchema.options.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="flex h-10 w-full items-center justify-between gap-3 rounded-md border bg-background px-3">
                <label
                  htmlFor="lts-exclude-internal-users"
                  className="text-xs font-medium leading-tight"
                >
                  Hide employees/dev
                </label>
                <Switch
                  id="lts-exclude-internal-users"
                  checked={excludeInternalUsers}
                  onCheckedChange={(checked) => {
                    updateSearch({
                      excludeInternalUsers: checked ? true : undefined,
                      expandedResultId: undefined,
                    });
                  }}
                />
              </div>
            </div>
            <div className="flex items-end">
              <Button onClick={applyFilters}>Search</Button>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertCircleIcon className="size-4" />
          {error.message}
        </div>
      )}

      {hasActiveClientFilters && isResultPageSaturated && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
          Filters are applied to the first {LONG_TO_SHORT_SEARCH_PAGE_SIZE} requests returned for
          this date range. Narrow the date range if a specific ID is missing.
        </div>
      )}

      <div className="rounded-md border">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-20">Platform</TableHead>
              <TableHead className="w-28">Downloaded</TableHead>
              <TableHead>Project Group</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Workflow ID</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Latency</TableHead>
              <TableHead>Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={10} className="h-28 text-center text-muted-foreground">
                  Loading long-to-short requests...
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              excludeInternalUsers &&
              (isFetchingInternalAccounts || isFetchingInternalUserLookups) && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-xs text-muted-foreground">
                    Syncing internal account filter...
                  </TableCell>
                </TableRow>
              )}
            {!isLoading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="h-28 text-center text-muted-foreground">
                  No long-to-short requests found for this range
                </TableCell>
              </TableRow>
            )}
            {items.map((item) => {
              const groupId = item.projectGroupId ?? null;
              const ownerUserId = item.userId ?? null;
              const expandedResultId = item.resultId;
              const isExpanded = groupId ? activeExpandedResultId === expandedResultId : false;
              const source = projectGroupSource(item.projectGroupMetadata);
              return (
                <Fragment key={item.resultId}>
                  <TableRow
                    key={item.resultId}
                    aria-expanded={groupId ? isExpanded : undefined}
                    className={groupId ? "cursor-pointer hover:bg-muted/50" : undefined}
                    data-project-group-id={groupId ?? undefined}
                    data-project-group-row={groupId ? "true" : undefined}
                    onClick={() => {
                      if (groupId) {
                        toggleExpanded(expandedResultId);
                      }
                    }}
                  >
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="size-8 p-0"
                        disabled={!groupId}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (groupId) {
                            toggleExpanded(expandedResultId);
                          }
                        }}
                        title={
                          groupId
                            ? "Expand project group"
                            : "Project group ID not returned by search response"
                        }
                      >
                        {isExpanded ? (
                          <ChevronDownIcon className="size-4" />
                        ) : (
                          <ChevronRightIcon className="size-4" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Badge className={stateBadgeClass(item.editState)}>{item.editState}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{formatPlatform(item.platform)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={item.hasDownloadEvent ? "default" : "outline"}>
                        {item.hasDownloadEvent ? "Downloaded" : "Not downloaded"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex max-w-72 flex-col gap-1">
                        <span className="truncate font-medium">
                          <CopyableId
                            value={groupId}
                            label="Project group ID"
                            fallback="Project group ID unavailable"
                          />
                        </span>
                        {item.error && (
                          <span className="max-w-72 truncate text-xs text-destructive">
                            {item.errorCode ? `${item.errorCode}: ` : ""}
                            {item.error}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-64 font-mono text-xs">
                      <CopyableId
                        value={source?.value}
                        label={source?.label ?? "Source"}
                        prefix={source?.prefix}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      <CopyableId value={item.workflowId} label="Workflow ID" />
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      <CopyableId value={item.userId} label="User ID" />
                    </TableCell>
                    <TableCell>{formatDuration(latencyMs(item))}</TableCell>
                    <TableCell>{formatDate(item.updatedAt)}</TableCell>
                  </TableRow>
                  {isExpanded && groupId && (
                    <TableRow key={`${item.resultId}-expanded`}>
                      <TableCell
                        colSpan={10}
                        className="max-w-0 overflow-hidden whitespace-normal bg-muted/30 p-0"
                      >
                        {ownerUserId ? (
                          <ProjectGroupExpansion
                            projectGroupId={groupId}
                            ownerUserId={ownerUserId}
                            ownerOrgId={item.orgId}
                            sourceMetadata={item.projectGroupMetadata}
                            returnSearch={projectViewerReturnSearch}
                          />
                        ) : (
                          <div className="p-4 text-sm text-destructive">
                            Project owner user ID unavailable.
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ProjectGroupExpansion({
  projectGroupId,
  ownerUserId,
  ownerOrgId,
  sourceMetadata,
  returnSearch,
}: {
  projectGroupId: string;
  ownerUserId: string;
  ownerOrgId?: string | null;
  sourceMetadata?: ProjectGroupMetadata | null;
  returnSearch: ReturnType<typeof buildProjectViewerReturnSearch>;
}) {
  const { data, isLoading, error } = fetchLongToShortProjectGroupQuery.useQuery({
    pathParams: { projectGroupId },
    params: {
      ownerUserId,
      ownerOrgId: ownerOrgId ?? undefined,
    },
  });
  const sourceAssetId = data?.metadata?.sourceAssetId ?? sourceMetadata?.sourceAssetId ?? null;

  if (isLoading) {
    return (
      <div className="min-w-0 max-w-full space-y-3 overflow-hidden p-4">
        <ProjectGroupOriginalAsset sourceAssetId={sourceAssetId} />
        <div className="text-sm text-muted-foreground">Loading project group...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-w-0 max-w-full space-y-3 overflow-hidden p-4">
        <ProjectGroupOriginalAsset sourceAssetId={sourceAssetId} />
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircleIcon className="size-4" />
          {error?.message ?? "Failed to load project group"}
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0 max-w-full space-y-3 overflow-hidden p-4">
      <ProjectGroupSummary group={data} />
      <ProjectGroupOriginalAsset sourceAssetId={sourceAssetId} />
      <div className="grid min-w-0 max-w-full gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(300px,100%),1fr))]">
        {data.items.map((item, index) => (
          <ProjectGroupItemRow
            key={item.metadata.outputAssetId}
            item={item}
            index={index}
            ownerUserId={ownerUserId}
            ownerOrgId={ownerOrgId}
            returnSearch={returnSearch}
          />
        ))}
        {data.items.length === 0 && (
          <div className="rounded-md border bg-background p-4 text-sm text-muted-foreground">
            No projects in this group.
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectGroupOriginalAsset({ sourceAssetId }: { sourceAssetId?: string | null }) {
  if (!sourceAssetId) {
    return null;
  }

  return (
    <div className="min-w-0 max-w-full rounded-md border bg-background p-3">
      <div className="flex min-w-0 flex-col gap-3 md:flex-row md:items-start">
        <div className="aspect-video w-full max-w-full overflow-hidden rounded-md border bg-black md:max-w-sm">
          <WorkflowAssetPreview assetId={sourceAssetId} controls mode="table" resolver="oasis" />
        </div>
        <div className="min-w-0 space-y-2 text-sm">
          <p className="font-medium">Original asset</p>
          <p className="text-xs text-muted-foreground">
            <CopyableId value={sourceAssetId} label="Original asset ID" prefix="Asset" />
          </p>
        </div>
      </div>
    </div>
  );
}

function ProjectGroupSummary({ group }: { group: ProjectGroup }) {
  const progress = group.status?.progress;

  return (
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <span className="font-medium">{group.title ?? "Untitled project group"}</span>
      {group.status && (
        <Badge className={stateBadgeClass(group.status.state)}>{group.status.state}</Badge>
      )}
      {progress != null && <span className="text-muted-foreground">{progress}%</span>}
      <span className="text-xs text-muted-foreground">
        <CopyableId value={group.id} label="Project group ID" />
      </span>
      {group.status?.error && (
        <span className="text-xs text-destructive">
          {group.status.errorCode ? `${group.status.errorCode}: ` : ""}
          {group.status.error}
        </span>
      )}
    </div>
  );
}

function ProjectGroupItemRow({
  item,
  index,
  ownerUserId,
  ownerOrgId,
  returnSearch,
}: {
  item: ProjectGroupItem;
  index: number;
  ownerUserId?: string | null;
  ownerOrgId?: string | null;
  returnSearch: ReturnType<typeof buildProjectViewerReturnSearch>;
}) {
  const label = item.metadata.title ?? `Generated project ${index + 1}`;
  const previewStyle = previewStyleForAspectRatio(item.metadata.outputAspectRatio);
  const viewerProjectId = item.projectId;

  return (
    <div className="flex min-w-0 max-w-full flex-col overflow-hidden rounded-md border bg-background p-3">
      <div className="flex min-w-0 justify-center">
        <div className="max-w-full overflow-hidden rounded-md border bg-black" style={previewStyle}>
          <WorkflowAssetPreview
            assetId={item.metadata.outputAssetId}
            controls
            mode="table"
            resolver="oasis"
          />
        </div>
      </div>
      <div className="mt-3 min-w-0 space-y-2">
        <p className="truncate text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">
          <CopyableId value={item.projectId} label="Project ID" prefix="Project" />
        </p>
        <p className="text-xs text-muted-foreground">
          <CopyableId value={item.metadata.outputAssetId} label="Asset ID" prefix="Asset" />
        </p>
        <Badge variant={item.hasDownloadEvent ? "default" : "outline"} className="w-fit">
          {item.hasDownloadEvent ? "Downloaded" : "Not downloaded"}
        </Badge>
        <Button variant="outline" size="sm" asChild>
          <Link
            to="/projects/$projectId"
            params={{ projectId: viewerProjectId }}
            search={{
              ...returnSearch,
              ownerUserId: ownerUserId ?? undefined,
              ownerOrgId: ownerOrgId ?? undefined,
            }}
          >
            <ExternalLinkIcon className="size-4" />
            Open project
          </Link>
        </Button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Score</p>
          <p>{item.metadata.score}</p>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Aspect Ratio</p>
          <p>{item.metadata.outputAspectRatio}</p>
        </div>
      </div>
      <div className="mt-3 min-w-0 space-y-1 text-sm">
        <p className="text-xs font-medium text-muted-foreground">Source Ranges</p>
        <div className="flex flex-wrap gap-1">
          {item.metadata.sourceRanges.map((range) => (
            <Badge
              key={`${range.startMs}-${range.endMs}`}
              variant="outline"
              className="font-mono text-[11px]"
            >
              {formatSourceTimestamp(range.startMs)} - {formatSourceTimestamp(range.endMs)}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}
